import type { Moment } from 'moment';
import { getDurationBySecond } from '@/shared/lib/aiUtils';

/**
 * Reports whether two epoch ranges intersect. Compares each range's start against the other's stop inclusively.
 *
 * @param range1Start - Start of the first range in epoch milliseconds.
 * @param range1Stop - End of the first range in epoch milliseconds.
 * @param range2Start - Start of the second range in epoch milliseconds.
 * @param range2Stop - End of the second range in epoch milliseconds.
 * @returns True when the two ranges share at least one instant.
 */
export const isRangesOverlap = (
  range1Start: number,
  range1Stop: number,
  range2Start: number,
  range2Stop: number,
) => range1Start <= range2Stop && range1Stop >= range2Start;

/**
 * Selects the recording segments overlapping a moment-bounded window. Converts both bounds to epochs and filters the summary data with isRangesOverlap.
 *
 * @param summaries - Recording summary carrying the data segments to search.
 * @param start - Start of the window as a moment.
 * @param stop - End of the window as a moment.
 * @returns The array of summary segments overlapping the window.
 */
export const getDataRange = (summaries: { data: any[] }, start: Moment, stop: Moment) => {
  const startEpoch = start.valueOf();
  const stopEpoch = stop.valueOf();
  const ranges = summaries.data.filter((x) => {
    const itemStartEpoch = new Date(x.start).getTime();
    const itemStopEpoch = new Date(x.stop).getTime();
    return isRangesOverlap(itemStartEpoch, itemStopEpoch, startEpoch, stopEpoch);
  });
  return ranges;
};
/**
 * Selects the recording segments covering a time window and reports whether they cover it without gaps. Filters the summary data to segments overlapping the window, sorts them by start, walks them tracking the furthest covered epoch, and returns empty ranges when a gap is found before the window's stop.
 *
 * @param summaries - Recording summary carrying the data segments to search.
 * @param start - Start of the window, as a date, epoch or moment.
 * @param stop - End of the window, as a date, epoch or moment.
 * @returns An object carrying the covering ranges and an isContinuous flag, empty and false when coverage is broken.
 */
export const getDataRangeWithin = (
  summaries: { data: any[] },
  start: string | number | Date | moment.Moment,
  stop: string | number | Date | moment.Moment,
) => {
  const startEpoch = new Date(start as any).getTime();
  const stopEpoch = new Date(stop as any).getTime();
  const overlappingRanges = summaries.data.filter((range) => {
    const itemStartEpoch = new Date(range.start).getTime();
    const itemStopEpoch = new Date(range.stop).getTime();
    return itemStopEpoch > startEpoch && itemStartEpoch < stopEpoch;
  });
  const sortedRanges = overlappingRanges.sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
  );
  const result = sortedRanges.reduce(
    (acc, range) => {
      const itemStartEpoch = new Date(range.start).getTime();
      const itemStopEpoch = new Date(range.stop).getTime();
      // Check if there is a gap between currentEpoch and the start of the current range
      if (acc.currentEpoch < itemStartEpoch) {
        acc.isContinuous = false;
        return acc;
      }
      // Update currentEpoch to the end of the current range if it extends further
      if (acc.currentEpoch < itemStopEpoch) {
        acc.currentEpoch = itemStopEpoch;
      }
      // Arr of ranges that have covered the entire stop time, set isContinuous to true
      if (acc.currentEpoch >= stopEpoch) {
        acc.isContinuous = true;
      }
      acc.ranges.push(range);
      return acc;
    },
    { ranges: [] as any[], isContinuous: false, currentEpoch: startEpoch },
  );
  return result.isContinuous
    ? { ranges: result.ranges, isContinuous: true }
    : { ranges: [], isContinuous: false };
};

/**
 * Totals how much recorded time falls inside a window across all overlapping segments. Sums each segment's clipped overlap in milliseconds, caps the total at the window length, and formats the seconds through getDurationBySecond.
 *
 * @param summaries - Recording summary carrying the data segments to measure.
 * @param start - Start of the window, as a date or epoch.
 * @param stop - End of the window, as a date or epoch.
 * @returns The formatted duration of recorded time inside the window.
 */
export const getTotalTimeWithin = (
  summaries: { data?: any[] },
  start: string | number | Date,
  stop: string | number | Date,
) => {
  const startEpoch = new Date(start).getTime();
  const stopEpoch = new Date(stop).getTime();

  // Calculate total time covered by overlapping ranges use reduce
  const totalMilliseconds = summaries?.data?.reduce((total, range) => {
    const itemStartEpoch = new Date(range.start).getTime();
    const itemStopEpoch = new Date(range.stop).getTime();
    if (itemStopEpoch > startEpoch && itemStartEpoch < stopEpoch) {
      const overlapStart = Math.max(itemStartEpoch, startEpoch);
      const overlapStop = Math.min(itemStopEpoch, stopEpoch);
      return total + (overlapStop - overlapStart);
    }
    return total;
  }, 0);

  const clampedMilliseconds = Math.min(totalMilliseconds, stopEpoch - startEpoch);
  const totalSeconds = clampedMilliseconds / 1000;
  return getDurationBySecond(totalSeconds);
};
